/**
 * TEXTOS FIXOS DAS SEÇÕES DA HOME — por codsite.
 *
 * Título/subtítulo/descrição das seções "Depoimentos" e "Blog" e o rótulo do link
 * "Ver todos" da vitrine de empreendimentos são hardcode compartilhado pelos ~300
 * sites: não há coluna em `tbl_sites` nem campo no Personalizar Site para eles. Quando
 * um cliente pede o texto dele, o pedido entra AQUI — num arquivo só, auditável, com o
 * codsite na chave —, nunca trocando o texto padrão de todo mundo. Mesmo molde de
 * `utils/tiposBuscaOcultos.ts` e `utils/site2524WhatsApp.ts`.
 *
 * Fora do mapa nada muda: cada componente cai no texto padrão de sempre.
 *
 * cs2593 (Cátia Caires — Maceió/AL, ticket #1385): a cliente enviou em 02/09/2026 um
 * documento com a reescrita da home ("NOVA HOME — CÁTIA CAIRES"). Os textos abaixo são
 * os das seções 5, 7 e 10 desse documento, verbatim. O título da vitrine de
 * empreendimentos fica em `VENTURES_TITULO_POR_TENANT` (pages/index.tsx), que já existia.
 */
export type TextosSecoes = {
    /** rótulo do link ao lado do título da vitrine de empreendimentos (padrão: "Ver todos") */
    empreendimentosVerTodos?: string;
    /** DepoimentosCard2 (modelo_depoimento = 2): tarja pequena, título grande e parágrafo */
    depoimentosTarja?: string;
    depoimentosTitulo?: string;
    depoimentosDescricao?: string;
    /** BlogCarousel (modelo_blogs = 2): tarja pequena, título grande e parágrafo */
    blogTarja?: string;
    blogTitulo?: string;
    blogDescricao?: string;
    /** ManagerCard/ManagerGrid (seção "Nossa equipe" da home) — padrão: "NOSSA EQUIPE" e variações por id */
    equipeTitulo?: string;
    /** NoticePieces: título da faixa de vídeos do YouTube (padrão: "NOSSO CANAL") */
    canalTitulo?: string;
    /**
     * TÍTULOS EM FRASE: títulos de seção (home, rodapé e blocos da ficha do imóvel) saem com a 1ª letra
     * maiúscula e o resto minúsculo — inclusive os que vêm do cadastro (nome do carrossel, "Quem somos")
     * e os que o CSS padrão põe em caixa alta (tarja do blog, colunas do rodapé). Ver `tituloEmFrase`.
     */
    titulosEmFrase?: boolean;
    /**
     * MegaFooter (rodape = 4): frase da coluna da marca no lugar do padrão
     * "<empresa> — imóveis para venda e locação em <cidade> e região." (que não serve a quem só vende).
     */
    rodapeFrase?: string;
    /** MegaFooter: UF no fim da linha do endereço ("… - Águas Claras - Viamão - RS"). Padrão: sem UF. */
    rodapeEnderecoUf?: string;
};

export const TEXTOS_SECOES_POR_SITE: Record<number, TextosSecoes> = {
    // cs2639 (CASAMAIS+, pedido de 16/09/2026): "NOSSO CANAL" vira "Vídeos em destaque" e TODOS os
    // títulos de seção seguem o padrão 1ª letra maiúscula e as outras minúsculas.
    2639: {
        equipeTitulo: 'Nossa equipe',
        canalTitulo: 'Vídeos em destaque',
        blogTarja: 'Blog',
        titulosEmFrase: true,
    },
    // cs2681 (Cardoso Corretor de Imóveis Rurais, pedidos de 30/10 a 01/11/2026 no grupo de implantação): só vendas —
    // frase do rodapé no texto do cliente e endereço terminando em "- RS".
    2681: {
        rodapeFrase:
            'Cardoso Corretor de Imóveis Rurais — Venda de Sítios, Chácaras, Fazendas e Áreas Rurais em Águas Claras, Viamão e Região. Somos Especialista em Imóveis Rurais. Cadastro e Regularização de Propriedades Rurais.',
        rodapeEnderecoUf: 'RS',
    },
    // cs2641 (DUE Imóveis, pedido de 01/10/2026): a seção de corretores da home chama "CONHEÇA A DUE".
    2641: {
        equipeTitulo: 'CONHEÇA A DUE',
    },
    2593: {
        empreendimentosVerTodos: 'Conhecer empreendimentos',
        depoimentosTarja: 'EXPERIÊNCIAS',
        depoimentosTitulo: 'Confiança construída em cada negociação.',
        depoimentosDescricao:
            'Experiências de clientes que confiaram em minha consultoria para decisões imobiliárias importantes.',
        blogTarja: 'CONTEÚDO',
        blogTitulo: 'Informação para boas decisões imobiliárias.',
        blogDescricao:
            'Mercado, investimento e estilo de vida em Maceió sob o olhar de quem vive o mercado imobiliário todos os dias.',
    },
};

/**
 * "NOSSA EQUIPE" → "Nossa equipe" · "Destaques de Vendas" → "Destaques de vendas".
 * Só a 1ª letra (depois de espaços) sobe; todo o resto desce, em pt-BR.
 */
export function tituloEmFrase(texto: unknown): string {
    const baixo = String(texto ?? '').toLocaleLowerCase('pt-BR');
    const i = baixo.search(/\S/);
    if (i < 0) return baixo;
    return baixo.slice(0, i) + baixo.charAt(i).toLocaleUpperCase('pt-BR') + baixo.slice(i + 1);
}

/** Aplica `tituloEmFrase` só para quem pediu (`titulosEmFrase`); os demais recebem o texto intacto. */
export function tituloDaSecao(codsite: unknown, texto: string): string {
    return textosSecoesDoSite(codsite).titulosEmFrase ? tituloEmFrase(texto) : texto;
}

/** Textos do tenant, ou objeto vazio (todo componente cai no seu texto padrão). */
export function textosSecoesDoSite(codsite: unknown): TextosSecoes {
    return TEXTOS_SECOES_POR_SITE[Number(codsite) || 0] || {};
}
